/*
  Mug cart drawer controller.

  Responsibilities:
    1. Drawer open/close (overlay click, ESC, [data-cart-open], [data-cart-close])
    2. AJAX cart mutations against /cart/*.js, with Section Rendering API
       re-render of the drawer fragment
    3. cart:updated DOM event dispatch — Phase 2's header-cart-count.js
       listens and updates the badge
    4. Cross-tab sync via localStorage 'storage' events — a mutation in
       tab A writes a fingerprint, tab B sees the change and refetches
       its own drawer state

  NOT named cart.js: Dawn ships its own assets/cart.js for the /cart page's
  <cart-items> Web Component (Phase 10). Overwriting that would break the
  cart-page line-item editor.

  Exposes window.MugCart = { open, close, add, change, remove, refetch }
  for Phase 5 quick-add and Phase 8 product form to call.
*/

(() => {
  const SECTION_ID = 'cart-drawer';
  const FINGERPRINT_KEY = 'mug_cart_fingerprint';

  let lastFingerprint = '';
  let inflight = null;

  const $drawer = () => document.querySelector('[data-cart-drawer]');

  // ─── Open / close ───────────────────────────────────────────────────
  const open = () => {
    const d = $drawer();
    if (!d) return;
    d.dataset.open = 'true';
    d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  };

  const close = () => {
    const d = $drawer();
    if (!d) return;
    d.dataset.open = 'false';
    d.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  // ─── Cross-tab sync ─────────────────────────────────────────────────
  // Writes a fingerprint to localStorage after every mutation. Other tabs
  // listen for 'storage' events on this key and refetch their own drawer
  // so a cart change in tab A surfaces in tab B without a full reload.
  const writeFingerprint = (cart) => {
    const fp = `${cart.token || ''}:${cart.item_count}:${Date.now()}`;
    lastFingerprint = fp;
    try { localStorage.setItem(FINGERPRINT_KEY, fp); } catch (e) { /* private mode */ }
  };

  window.addEventListener('storage', (e) => {
    if (e.key !== FINGERPRINT_KEY) return;
    if (!e.newValue || e.newValue === lastFingerprint) return;
    // Another tab mutated the cart — refetch our drawer fragment, no event re-dispatch.
    refetch({ silent: true });
  });

  // ─── DOM event dispatch ─────────────────────────────────────────────
  // Phase 2 header-cart-count.js listens for this on `document` and updates
  // the badge. Dispatched after every local mutation but NOT after a remote
  // (cross-tab) refresh — see the silent flag in refetch().
  const dispatchUpdate = (cart) => {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  };

  // ─── Section rendering ──────────────────────────────────────────────
  const swapDrawerHTML = (html) => {
    const wasOpen = $drawer()?.dataset.open === 'true';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector('[data-cart-drawer]');
    const current = $drawer();
    if (!fresh || !current) return;
    current.replaceWith(fresh);
    if (wasOpen) {
      fresh.dataset.open = 'true';
      fresh.setAttribute('aria-hidden', 'false');
    }
  };

  // ─── AJAX core ──────────────────────────────────────────────────────
  const postJSON = async (url, body) => {
    body.sections = SECTION_ID;
    body.sections_url = window.location.pathname;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || `Cart request failed: ${res.status}`);
    }
    return res.json();
  };

  const applyCartResponse = (data, { silent = false } = {}) => {
    if (data.sections && data.sections[SECTION_ID]) {
      swapDrawerHTML(data.sections[SECTION_ID]);
    }
    // /cart/add.js returns the added line item, not the cart — refetch /cart.js for the latest state.
    if (typeof data.item_count !== 'number') return refetchCartJSON({ silent });
    writeFingerprint(data);
    if (!silent) dispatchUpdate(data);
  };

  const refetchCartJSON = async ({ silent = false } = {}) => {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart = await res.json();
    writeFingerprint(cart);
    if (!silent) dispatchUpdate(cart);
  };

  // ─── Public API ─────────────────────────────────────────────────────
  const add = async (payload) => {
    // payload: { id, quantity, properties? } or FormData
    let body;
    if (payload instanceof FormData) {
      body = Object.fromEntries(payload.entries());
      body.quantity = Number(body.quantity || 1);
    } else {
      body = { ...payload };
    }
    inflight = postJSON('/cart/add.js', body).then(async (data) => {
      await applyCartResponse(data);
      open();
    });
    return inflight;
  };

  const change = async ({ key, quantity }) => {
    inflight = postJSON('/cart/change.js', { id: key, quantity }).then(applyCartResponse);
    return inflight;
  };

  const remove = (key) => change({ key, quantity: 0 });

  const refetch = async (opts = {}) => {
    const res = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`);
    const data = await res.json();
    if (data[SECTION_ID]) swapDrawerHTML(data[SECTION_ID]);
    await refetchCartJSON(opts);
  };

  // ─── Delegated DOM bindings ─────────────────────────────────────────
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      open();
      return;
    }
    if (e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-overlay]')) {
      e.preventDefault();
      close();
      return;
    }
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      remove(removeBtn.dataset.itemKey).catch(console.error);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && $drawer()?.dataset.open === 'true') close();
  });

  // Quantity stepper inputs (from snippets/quantity-stepper.liquid) bubble
  // 'change' events when − / + click. Each cart line wraps the stepper in
  // a [data-cart-item][data-item-key=...] element.
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-quantity-input]');
    if (!input) return;
    const line = input.closest('[data-cart-item]');
    if (!line) return;
    const key = line.dataset.itemKey;
    const quantity = Number(input.value);
    if (!Number.isFinite(quantity) || quantity < 0) return;
    change({ key, quantity }).catch(console.error);
  });

  // ─── Initial fingerprint so storage listeners have a baseline ───────
  // No mutation — just records what THIS tab considers the current state
  // so we don't false-positive on the storage event we ourselves caused.
  fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
    .then((r) => r.json())
    .then((cart) => writeFingerprint(cart))
    .catch(() => {});

  window.MugCart = { open, close, add, change, remove, refetch };
})();
